import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileImage, FolderOpen, Loader2, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileIcon, getMimeType } from '@/components/documents/FileIcon';
import FilePreview from '@/components/documents/FilePreview';
import type { FileSystemEntry } from '@/hooks/useFileSystem';
import {
  copyScannerFileToActivity,
  markScannerAssociated,
  useScannerInbox,
  type ConflictChoice,
  type ScannerFile,
} from '@/hooks/useScannerInbox';
import { useAuthStore } from '@/store/authStore';
import { activityService, type Activity } from '@/services/activityService';
import { cn } from '@/utils/cn';

type ListMode = 'queue' | 'associated';

const PREVIEW_EXT = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return '';
  return name.slice(dot + 1).toLowerCase();
}

function formatSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(ms: number): string {
  if (!ms) return '';
  return new Date(ms).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function activityLabel(activity: Activity): string {
  const practice = activity.practice;
  const number = practice?.practiceNumber ? `#${practice.practiceNumber}` : '';
  const client = practice?.client
    ? `${practice.client.firstName} ${practice.client.lastName}`.trim()
    : '';
  return [number, practice?.title, client, activity.title].filter(Boolean).join(' — ');
}

export default function ScannerPage() {
  const { user } = useAuthStore();
  const { isElectron, folder, files, error, pickFolder, associatedKeys } = useScannerInbox();
  const associated = useMemo(() => new Set(associatedKeys), [associatedKeys]);

  const [mode, setMode] = useState<ListMode>('queue');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [conflictName, setConflictName] = useState<string | null>(null);
  const [conflictResolve, setConflictResolve] = useState<((choice: ConflictChoice) => void) | null>(null);

  const visibleFiles = useMemo(
    () => files.filter((file) => (mode === 'queue' ? !associated.has(file.key) : associated.has(file.key))),
    [files, mode, associated]
  );

  const activeFile = files.find((file) => file.key === activeKey) ?? null;
  const selectedActivity = activities.find((activity) => activity.id === activityId) ?? null;
  const missingPath = !!selectedActivity && !selectedActivity.practice?.documentPath;

  useEffect(() => {
    setSelectedKeys(new Set());
    setActiveKey(null);
  }, [mode, folder]);

  const activePath = activeFile?.fullPath ?? null;
  const activeName = activeFile?.name ?? null;

  useEffect(() => {
    if (!activePath || !activeName || !isElectron || !window.electronFS) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPreviewBlob(null);
      return;
    }
    const ext = extensionOf(activeName);
    if (!PREVIEW_EXT.has(ext)) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPreviewBlob(null);
      return;
    }
    let url: string | null = null;
    setPreviewLoading(true);
    try {
      const buffer = window.electronFS.readFile(activePath);
      const blob = new Blob([buffer], { type: getMimeType(activeName) });
      url = URL.createObjectURL(blob);
      setPreviewBlob(blob);
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
      setPreviewBlob(null);
      toast.error('Impossibile leggere il file per l’anteprima');
    } finally {
      setPreviewLoading(false);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [activePath, activeName, isElectron]);

  useEffect(() => {
    if (!user?.id) return;
    const handle = window.setTimeout(() => {
      setActivitiesLoading(true);
      activityService
        .getActivities({
          userId: user.id,
          status: 'OPEN',
          search: search.trim() || undefined,
          limit: 50,
          page: 1,
        })
        .then((res) => setActivities(res.data || []))
        .catch(() => {
          setActivities([]);
          toast.error('Impossibile caricare le attività');
        })
        .finally(() => setActivitiesLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [user?.id, search]);

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const askConflict = useCallback((name: string) => {
    return new Promise<ConflictChoice>((resolve) => {
      setConflictName(name);
      setConflictResolve(() => resolve);
    });
  }, []);

  const settleConflict = (choice: ConflictChoice) => {
    conflictResolve?.(choice);
    setConflictResolve(null);
    setConflictName(null);
  };

  const runAssociate = async (complete: boolean) => {
    if (!selectedActivity || busy) return;
    const documentPath = selectedActivity.practice?.documentPath;
    if (!documentPath) return;
    const chosen = files.filter((file) => selectedKeys.has(file.key));
    if (chosen.length === 0) return;

    setBusy(true);
    const succeeded: string[] = [];
    let aborted = false;
    try {
      for (const file of chosen) {
        try {
          await copyScannerFileToActivity({
            sourcePath: file.fullPath,
            fileName: file.name,
            documentPath,
            subfolder: selectedActivity.type,
            onConflict: askConflict,
          });
          succeeded.push(file.key);
        } catch (err) {
          const message = err instanceof Error ? err.message : '';
          if (message === '__CANCEL__') {
            aborted = true;
            break;
          }
          toast.error(`${file.name}: ${message || 'copia non riuscita'}`);
        }
      }

      if (succeeded.length > 0) {
        markScannerAssociated(succeeded);
        setSelectedKeys((prev) => {
          const next = new Set(prev);
          for (const key of succeeded) next.delete(key);
          return next;
        });
      }

      const allOk = succeeded.length === chosen.length && !aborted;
      if (complete && allOk) {
        try {
          await activityService.completeActivity(selectedActivity.id);
          setActivities((prev) => prev.filter((activity) => activity.id !== selectedActivity.id));
          setActivityId(null);
          toast.success(
            succeeded.length === 1
              ? 'File associato e attività completata'
              : `${succeeded.length} file associati e attività completata`
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : 'errore sconosciuto';
          toast.error(`File copiati, attività non completata: ${message}`);
        }
      } else if (succeeded.length > 0 && !complete) {
        toast.success(
          succeeded.length === 1 ? 'File associato' : `${succeeded.length} file associati`
        );
      } else if (complete && succeeded.length > 0 && !allOk) {
        toast.error('Attività non completata: non tutti i file sono stati copiati');
      }
    } finally {
      setBusy(false);
    }
  };

  const openWithSystem = async (file: ScannerFile) => {
    try {
      await window.electronFS?.openPath(file.fullPath);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossibile aprire il file');
    }
  };

  const previewEntry: FileSystemEntry | null = activeFile
    ? {
        name: activeFile.name,
        kind: 'file',
        path: activeFile.fullPath,
        handle: null as unknown as FileSystemHandle,
        size: activeFile.size,
        lastModified: activeFile.lastModified,
      }
    : null;

  const ext = activeFile ? extensionOf(activeFile.name) : '';
  const canPreview = PREVIEW_EXT.has(ext);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScanLine className="h-7 w-7 text-blue-600" />
            Scanner
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            File nella cartella di scansione di questo computer. L’originale resta dov’è.
          </p>
        </div>
        {isElectron && (
          <Button variant="outline" onClick={() => void pickFolder()}>
            <FolderOpen className="h-4 w-4 mr-2" />
            {folder ? 'Cambia cartella' : 'Scegli cartella'}
          </Button>
        )}
      </div>

      {!isElectron && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          La cartella dello scanner è sul PC e si legge solo dall’app Studio Gori (MSI). Apri Studio Gori sul computer dove arriva la scansione.
        </div>
      )}

      {isElectron && !folder && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
          <p className="text-sm text-gray-600 mb-4">
            Nessuna cartella configurata su questo computer. Indica la cartella in cui lo scanner salva i file.
          </p>
          <Button onClick={() => void pickFolder()}>Scegli cartella</Button>
        </div>
      )}

      {isElectron && folder && (
        <>
          <p className="text-xs text-gray-500 font-mono break-all">{folder}</p>
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2">
            <Button
              variant={mode === 'queue' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('queue')}
            >
              In coda
            </Button>
            <Button
              variant={mode === 'associated' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('associated')}
            >
              Già associati
            </Button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-4 min-h-[32rem]">
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              {visibleFiles.length === 0 ? (
                <p className="p-6 text-sm text-gray-500">
                  {mode === 'queue' ? 'Nessun file nuovo in questa cartella.' : 'Nessun file già associato.'}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 max-h-[36rem] overflow-y-auto">
                  {visibleFiles.map((file) => {
                    const checked = selectedKeys.has(file.key);
                    const active = activeKey === file.key;
                    return (
                      <li key={file.key}>
                        <div
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 cursor-pointer',
                            active ? 'bg-blue-50' : 'hover:bg-gray-50'
                          )}
                          onClick={() => setActiveKey(file.key)}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            onChange={() => toggleKey(file.key)}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={`Seleziona ${file.name}`}
                          />
                          <FileIcon name={file.name} kind="file" size={18} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">
                              {formatSize(file.size)}
                              {file.lastModified ? ` · ${formatWhen(file.lastModified)}` : ''}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white flex flex-col min-h-[24rem]">
              <div className="flex-1 border-b border-gray-100 min-h-[16rem] bg-gray-50">
                {!activeFile && (
                  <p className="p-6 text-sm text-gray-500">Seleziona un file per l’anteprima.</p>
                )}
                {activeFile && previewLoading && (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                )}
                {activeFile && !previewLoading && canPreview && previewUrl && (
                  ext === 'pdf' ? (
                    <iframe title={activeFile.name} src={previewUrl} className="w-full h-full min-h-[16rem] border-0" />
                  ) : (
                    <div className="h-full min-h-[16rem] flex items-center justify-center p-4">
                      <img src={previewUrl} alt={activeFile.name} className="max-h-80 max-w-full object-contain" />
                    </div>
                  )
                )}
                {activeFile && !previewLoading && !canPreview && (
                  <div className="h-full min-h-[16rem] flex flex-col items-center justify-center gap-3 text-gray-500 p-6 text-center">
                    <FileImage className="h-10 w-10" />
                    <p className="text-sm">Anteprima non disponibile per questo tipo di file.</p>
                    <Button variant="outline" size="sm" onClick={() => void openWithSystem(activeFile)}>
                      Apri con il sistema
                    </Button>
                  </div>
                )}
              </div>

              {activeFile && canPreview && previewBlob && (
                <div className="px-3 py-2 border-b border-gray-100">
                  <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(true)}>
                    Anteprima grande
                  </Button>
                </div>
              )}

              <div className="p-3 space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500" htmlFor="scanner-activity-search">
                    Attività assegnate a te, in attesa o in corso
                  </label>
                  <Input
                    id="scanner-activity-search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Cerca pratica, cliente o titolo…"
                    className="mt-1"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto rounded border border-gray-200">
                  {activitiesLoading && (
                    <p className="p-3 text-sm text-gray-500">Caricamento…</p>
                  )}
                  {!activitiesLoading && activities.length === 0 && (
                    <p className="p-3 text-sm text-gray-500">Nessuna attività aperta assegnata a te.</p>
                  )}
                  {activities.map((activity) => (
                    <button
                      key={activity.id}
                      type="button"
                      onClick={() => setActivityId(activity.id)}
                      className={cn(
                        'block w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-0',
                        activityId === activity.id ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'
                      )}
                    >
                      <span className="font-medium">{activity.title}</span>
                      <span className="block text-xs text-gray-500 truncate">{activityLabel(activity)}</span>
                    </button>
                  ))}
                </div>

                {missingPath && selectedActivity && (
                  <p className="text-sm text-amber-700">
                    Questa pratica non ha una cartella documenti.{' '}
                    <Link className="underline" to={`/practices/${selectedActivity.practiceId}?tab=documents`}>
                      Configurala nella pratica
                    </Link>
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={busy || selectedKeys.size === 0 || !selectedActivity || missingPath}
                    onClick={() => void runAssociate(false)}
                  >
                    Associa
                  </Button>
                  <Button
                    variant="outline"
                    disabled={busy || selectedKeys.size === 0 || !selectedActivity || missingPath}
                    onClick={() => void runAssociate(true)}
                  >
                    Associa e completa
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {conflictName && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg space-y-4">
            <h2 className="text-base font-semibold text-gray-900">File già presente</h2>
            <p className="text-sm text-gray-600">
              Nella cartella dell’attività esiste già <span className="font-mono">{conflictName}</span>.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => settleConflict('cancel')}>Annulla</Button>
              <Button variant="outline" onClick={() => settleConflict('rename')}>Rinomina</Button>
              <Button onClick={() => settleConflict('overwrite')}>Sovrascrivi</Button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && previewEntry && previewBlob && (
        <FilePreview
          entry={previewEntry}
          blob={previewBlob}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
